import {
  GeographicOption,
  type GeographicTargetingApi,
  type GeographicTargetingState,
} from '@openx/types/targeting/geographic';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { getDefaultGeographicState } from '../constants';
import {
  getEmptyLocationState,
  transformApiBoundingBoxToStateObject,
  transformApiCirclesToStateObject,
  transformApiLocationSourceToStateObject,
  transformGeoToStateObject,
} from '../utils';

export const useGetGeoState = (targetingParams: GeographicTargetingApi | null) => {
  const [geographicBaseState, setGeographicBaseState] = useState<GeographicTargetingState | null>(null);
  const [geographicState, setGeographicState] = useState<GeographicTargetingState>(getDefaultGeographicState);
  const previousTargetingParamsRef = useRef<GeographicTargetingApi | null | undefined>(undefined);

  const isDataUpdated = useMemo(
    () => !isEqual(geographicBaseState, geographicState),
    [geographicBaseState, geographicState]
  );

  const { useFetchByGeoData } = useTargetingContext();
  const { fetchByGeoData } = useFetchByGeoData();

  const onTargetingParamsChange = useCallback(
    async (targetingParams: GeographicTargetingApi | null) => {
      const { includes, excludes } = targetingParams || {};

      const [fetchedIncludes, fetchedExcludes] = await Promise.all([
        includes ? fetchByGeoData(includes) : [],
        excludes ? fetchByGeoData(excludes) : [],
      ]);

      const containsSubset = fetchedIncludes.length && fetchedExcludes.length;

      const newState = {} as GeographicTargetingState;

      newState[GeographicOption.BOUNDING_BOX] = transformApiBoundingBoxToStateObject(targetingParams);
      newState[GeographicOption.CIRCLES] = transformApiCirclesToStateObject(
        targetingParams?.[GeographicOption.CIRCLES]
      );
      newState[GeographicOption.INCLUDES] = transformGeoToStateObject(fetchedIncludes);
      newState[GeographicOption.SOURCE] = transformApiLocationSourceToStateObject(targetingParams?.location_source);

      if (containsSubset) {
        newState[GeographicOption.EXCLUDES] = getEmptyLocationState();
        newState[GeographicOption.EXCLUDES_SUBSET] = fetchedExcludes;
      } else {
        newState[GeographicOption.EXCLUDES] = transformGeoToStateObject(fetchedExcludes);
        newState[GeographicOption.EXCLUDES_SUBSET] = [];
      }

      setGeographicBaseState(prev => (prev && isEqual(prev, newState) ? prev : newState));
      setGeographicState(prev => (isEqual(prev, newState) ? prev : newState));
    },
    [fetchByGeoData]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: onTargetingParamsChange intentionally excluded to prevent re-fetching when callback changes
  useEffect(() => {
    // Skip fetch if targetingParams hasn't changed by value (prevents redundant API calls when parent re-renders with same data)
    if (isEqual(previousTargetingParamsRef.current, targetingParams)) {
      return;
    }
    previousTargetingParamsRef.current = targetingParams;
    onTargetingParamsChange(targetingParams);
  }, [targetingParams]);

  return { geographicBaseState, geographicState, isDataUpdated, setGeographicBaseState, setGeographicState };
};
